import { makeAutoObservable, runInAction } from "mobx";

export type ResourceStatus = "idle" | "loading" | "success" | "error";

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

interface ResourceOptions<T, K> {
    /** Compares two keys; by default their JSON */
    sameKey?: (a: K, b: K) => boolean;
    /** Data to show before the first load has finished, e.g. from a cache. The first load replaces it. */
    initial?: T;
}

/**
 * One piece of server data, for the key it was last loaded with (an id, a slug, a filter, ...).
 *
 * It replaces the `loaded...ForId` / `loading...` flags the stores keep by hand:
 * - `load(key)` does nothing when `key` is already loaded or loading, so views can call it freely
 * - a new key drops the old data right away instead of showing another category's episodes
 * - when requests overlap, only the newest one is applied
 * - `load` never rejects: read `status` and `error`
 *
 * It knows nothing about HTTP; the fetcher is any function that returns a promise.
 */
export class Resource<T, K = void> {
    data: T | undefined;
    error: unknown = null;
    status: ResourceStatus = "idle";

    private key: K | undefined = undefined;
    private hasKey = false;
    private token = 0;
    private inFlight: Promise<void> | null = null;
    private readonly sameKey: (a: K, b: K) => boolean;

    constructor(
        private readonly fetcher: (key: K) => Promise<T>,
        options: ResourceOptions<T, K> = {}
    ) {
        this.sameKey = options.sameKey ?? sameJson;
        this.data = options.initial;
        makeAutoObservable<Resource<T, K>, "fetcher" | "sameKey" | "key" | "hasKey" | "token" | "inFlight">(
            this,
            { fetcher: false, sameKey: false, key: false, hasKey: false, token: false, inFlight: false },
            // data is replaced, never mutated: watching the reference is enough and cheap for long lists
            { deep: false }
        );
    }

    get loading() {
        return this.status === "loading";
    }

    get loaded() {
        return this.status === "success";
    }

    /** True when `key` is the one this resource holds (or is fetching) */
    isFor(key: K) {
        return this.hasKey && this.sameKey(this.key as K, key);
    }

    load(key: K): Promise<void> {
        if (this.isFor(key) && this.status !== "error") {
            return this.inFlight ?? Promise.resolve();
        }
        return this.start(key);
    }

    /** Resolves when the load that is running now has finished, without starting one. Never rejects. */
    whenSettled(): Promise<void> {
        return this.inFlight ?? Promise.resolve();
    }

    /** Fetches again, keeping the current data on screen until the new data arrives */
    refresh(): Promise<void> {
        return this.hasKey ? this.start(this.key as K) : Promise.resolve();
    }

    reset() {
        this.token += 1;
        this.inFlight = null;
        this.hasKey = false;
        this.key = undefined;
        this.data = undefined;
        this.error = null;
        this.status = "idle";
    }

    private start(key: K): Promise<void> {
        const replacesOtherKey = this.hasKey && !this.isFor(key);
        const mine = ++this.token;
        this.key = key;
        this.hasKey = true;
        this.error = null;
        this.status = "loading";
        if (replacesOtherKey) {
            this.data = undefined;
        }

        const request = this.fetcher(key).then(
            (data) => {
                if (mine !== this.token) {
                    return;
                }
                runInAction(() => {
                    this.data = data;
                    this.status = "success";
                    this.inFlight = null;
                });
            },
            (error: unknown) => {
                if (mine !== this.token) {
                    return;
                }
                runInAction(() => {
                    this.error = error;
                    this.status = "error";
                    this.inFlight = null;
                });
            }
        );
        this.inFlight = request;
        return request;
    }
}

/**
 * A list that is read page by page and appended to, for infinite scroll. Like Resource, but `items`
 * grows with `loadMore()` and `hasMore` turns false at the first page that comes back short.
 */
export class PagedResource<T, K = void> {
    items: T[] = [];
    error: unknown = null;
    status: ResourceStatus = "idle";
    hasMore = true;

    private key: K | undefined = undefined;
    private hasKey = false;
    private page = 0;
    private token = 0;
    private inFlight: Promise<void> | null = null;

    private readonly sameKey: (a: K, b: K) => boolean;

    constructor(
        private readonly fetchPage: (key: K, page: number) => Promise<T[]>,
        private readonly pageSize: number,
        options: Pick<ResourceOptions<T[], K>, "sameKey"> = {}
    ) {
        this.sameKey = options.sameKey ?? sameJson;
        makeAutoObservable<
            PagedResource<T, K>,
            "fetchPage" | "pageSize" | "sameKey" | "key" | "hasKey" | "page" | "token" | "inFlight"
        >(
            this,
            {
                fetchPage: false,
                pageSize: false,
                sameKey: false,
                key: false,
                hasKey: false,
                page: false,
                token: false,
                inFlight: false,
            },
            { deep: false }
        );
    }

    get loading() {
        return this.status === "loading";
    }

    get loaded() {
        return this.status === "success";
    }

    isFor(key: K) {
        return this.hasKey && this.sameKey(this.key as K, key);
    }

    /** Loads the first page for `key`, unless that key is already loaded or loading */
    load(key: K): Promise<void> {
        if (this.isFor(key) && this.status !== "error") {
            return this.inFlight ?? Promise.resolve();
        }
        this.token += 1;
        this.key = key;
        this.hasKey = true;
        this.page = 0;
        this.items = [];
        this.hasMore = true;
        return this.fetchNext();
    }

    /** Appends the next page. Ignored while a page is loading, or when there are no more */
    loadMore(): Promise<void> {
        if (!this.hasKey || !this.hasMore || this.status === "loading") {
            return this.inFlight ?? Promise.resolve();
        }
        return this.fetchNext();
    }

    reset() {
        this.token += 1;
        this.inFlight = null;
        this.hasKey = false;
        this.key = undefined;
        this.page = 0;
        this.items = [];
        this.hasMore = true;
        this.error = null;
        this.status = "idle";
    }

    private fetchNext(): Promise<void> {
        const mine = this.token;
        const page = this.page + 1;
        this.error = null;
        this.status = "loading";

        const request = this.fetchPage(this.key as K, page).then(
            (items) => {
                if (mine !== this.token) {
                    return;
                }
                runInAction(() => {
                    this.items = [...this.items, ...items];
                    this.page = page;
                    this.hasMore = items.length >= this.pageSize;
                    this.status = "success";
                    this.inFlight = null;
                });
            },
            (error: unknown) => {
                if (mine !== this.token) {
                    return;
                }
                runInAction(() => {
                    this.error = error;
                    this.status = "error";
                    this.inFlight = null;
                });
            }
        );
        this.inFlight = request;
        return request;
    }
}
